// src/components/calendar/DayDetailModal.tsx
import React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '../../theme/theme';
import { getCustomIcon } from '../../utils/customIcons';

export interface BookingEntry {
  id: number;
  label: string;
}

export interface DayDetailModalProps {
  open: boolean;
  date: string;
  checkIns: BookingEntry[];
  checkOuts: BookingEntry[];
  onClose: () => void;
  onPrevDay: () => void;
  onNextDay: () => void;
}

const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 8px 8px 4px',
  backgroundColor: colors.primary.main,
  color: colors.text.light,
  flexShrink: 0,
});

const Section = styled(Box)(({ theme }) => ({
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '12px',
});

const ClientName = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  padding: '10px 0',
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  '&:last-child': { borderBottom: 'none' },
  '&:hover': {
    color: colors.primary.main,
    paddingLeft: '8px',
    transition: 'all 0.15s ease',
  },
}));

const EmptyState = styled(Box)({
  padding: '40px 24px',
  textAlign: 'center',
  opacity: 0.5,
});

const NavButton = styled(IconButton)({
  color: colors.text.light,
  flexShrink: 0,
});

const DayDetailModal: React.FC<DayDetailModalProps> = ({
  open,
  date,
  checkIns,
  checkOuts,
  onClose,
  onPrevDay,
  onNextDay,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const formattedDate = dayjs(date).format('dddd, D MMMM YYYY');

  const handleBookingClick = (id: number) => {
    onClose();
    navigate(`/booking/${id}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : '8px', overflow: 'hidden' },
      }}
    >
      <ModalHeader>
        <NavButton onClick={onPrevDay} aria-label="Previous day">
          <ChevronLeftIcon sx={{ fontSize: '2.8rem' }} />
        </NavButton>

        <Typography
          sx={{
            fontSize: '1.8rem',
            fontWeight: 700,
            flex: 1,
            textAlign: 'center',
          }}
        >
          {formattedDate}
        </Typography>

        <NavButton onClick={onNextDay} aria-label="Next day">
          <ChevronRightIcon sx={{ fontSize: '2.8rem' }} />
        </NavButton>

        <NavButton onClick={onClose} aria-label="Close">
          <CloseIcon sx={{ fontSize: '2.4rem' }} />
        </NavButton>
      </ModalHeader>

      <DialogContent sx={{ padding: 0 }}>
        {checkIns.length > 0 && (
          <Section>
            <SectionHeader>
              <Box sx={{ display: 'flex', transform: 'scaleX(-1)' }}>
                {getCustomIcon('bigarrow', '2rem', colors.primary.main)}
              </Box>
              <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
                Check-ins ({checkIns.length})
              </Typography>
            </SectionHeader>
            {checkIns.map((entry) => (
              <ClientName
                key={entry.id}
                onClick={() => handleBookingClick(entry.id)}
              >
                {entry.label}
              </ClientName>
            ))}
          </Section>
        )}

        {checkOuts.length > 0 && (
          <Section>
            <SectionHeader>
              <Box sx={{ display: 'flex' }}>
                {getCustomIcon('bigarrow', '2rem', colors.tertiary.dark)}
              </Box>
              <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
                Check-outs ({checkOuts.length})
              </Typography>
            </SectionHeader>
            {checkOuts.map((entry) => (
              <ClientName
                key={entry.id}
                onClick={() => handleBookingClick(entry.id)}
              >
                {entry.label}
              </ClientName>
            ))}
          </Section>
        )}

        {checkIns.length === 0 && checkOuts.length === 0 && (
          <EmptyState>
            <Typography sx={{ fontSize: '1.6rem' }}>
              No activity for this date
            </Typography>
          </EmptyState>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DayDetailModal;
